/**
 * Eagerly syncs line item properties on every cart render:
 *
 * "PRE ORDER Ships by" — written when the variant is a preorder
 *   (inventory_policy=continue, qty<1, no no_preorder tag). Removed
 *   automatically when the product comes back in stock.
 *
 * "Final Sale" — written when the product is tagged "Final Sale".
 *   Value is "Engraved items cannot be returned." when the line item
 *   already carries an "Engraving:" property; otherwise "No Returns or Exchanges".
 *   Removed automatically when the tag is no longer present.
 *
 * Triggered on DOMContentLoaded and on every cart re-render via
 * MutationObserver (debounced 300ms). Fails silently so a network
 * error never blocks a sale.
 */

let isUpdating = false;

async function syncLineItemProperties() {
  if (isUpdating) return;

  const allRows = [...document.querySelectorAll('[data-key]')];
  if (allRows.length === 0) return;

  isUpdating = true;
  try {
    const cartResp = await fetch('/cart.js');
    const cart = await cartResp.json();

    const updates = [];

    for (const row of allRows) {
      const lineItem = cart.items.find((i) => i.key === row.dataset.key);
      if (!lineItem) continue;

      let props = { ...(lineItem.properties ?? {}) };
      let changed = false;

      // — PRE ORDER Ships by —
      const preorderText = row.dataset.preorderText ?? '';
      const currentPreorder = props['PRE ORDER Ships by'] ?? '';
      if (preorderText) {
        if (currentPreorder !== preorderText) {
          props['PRE ORDER Ships by'] = preorderText;
          changed = true;
        }
      } else if (currentPreorder !== '') {
        delete props['PRE ORDER Ships by'];
        changed = true;
      }

      // — Final Sale —
      // Engraving on any item triggers the engraving message regardless of tag.
      // The Final Sale tag alone (no engraving) triggers the generic message.
      const isFinalSale = row.dataset.finalSale === 'true';
      const hasEngraving = Object.keys(props).some((k) => k.toLowerCase().startsWith('engraving'));
      const currentFinalSale = props['Final Sale'] ?? '';
      if (hasEngraving) {
        const expectedValue = 'Engraved items cannot be returned.';
        if (currentFinalSale !== expectedValue) {
          props['Final Sale'] = expectedValue;
          changed = true;
        }
      } else if (isFinalSale) {
        const expectedValue = 'No Returns or Exchanges';
        if (currentFinalSale !== expectedValue) {
          props['Final Sale'] = expectedValue;
          changed = true;
        }
      } else if (currentFinalSale !== '') {
        delete props['Final Sale'];
        changed = true;
      }

      if (changed) {
        updates.push({ key: row.dataset.key, props });
      }
    }

    if (updates.length === 0) return;

    await Promise.all(
      updates.map(({ key, props }) =>
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, properties: props }),
        })
      )
    );

    // Tell Horizon's cart component to re-render so the updated properties
    // are immediately visible without a manual page refresh.
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: {} } }));
  } catch {
    // Fail silently — property sync is best-effort.
  } finally {
    isUpdating = false;
  }
}

// Run on initial page load (covers cart page).
document.addEventListener('DOMContentLoaded', syncLineItemProperties);

// Re-run whenever the cart re-renders (covers cart drawer and all updates).
// Debounced so rapid DOM mutations collapse into a single run.
let debounceTimer;
new MutationObserver(() => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(syncLineItemProperties, 300);
}).observe(document.body, { childList: true, subtree: true });
